/*
 * HomeFixer360 - AI Chatbot Component
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from './button';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hi! I\'m the HomeFixer360 assistant. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickQuestions = [
    "How does HomeFixer360 work?",
    "What plans do you offer?",
    "Which trades do you support?",
    "Do you offer team accounts?",
    "Tell me about the CRM"
  ];

  const knowledgeBase = {
    'platform': {
      keywords: ['platform', 'how', 'work', 'works', 'technology', 'about'],
      response: 'HomeFixer360 is an all-in-one CRM and estimation platform for home service pros. Build line-item estimates for any trade — kitchens, baths, HVAC, decks, windows, electrical, hardscaping, and more — and send branded proposals to your customers in minutes.'
    },
    'plans': {
      keywords: ['plan', 'pricing', 'cost', 'price', 'subscription', 'package'],
      response: 'We offer 3 plans:\n\n**Basic ($299/mo)**: 50 estimates/month, 3 users, AI chatbot support\n\n**Professional ($499/mo)**: 200 estimates/month, 7 users, priority support, remove branding\n\n**Elite ($999/mo)**: Unlimited estimates, unlimited users, priority phone support, white-label\n\nAll plans include the full CRM, line-item estimator, and branded proposals!'
    },
    'trial': {
      keywords: ['free', 'trial', 'demo', 'test', 'try'],
      response: 'You can start today:\n\n✅ Cancel anytime\n✅ 30-day money-back guarantee\n✅ Priority onboarding support\n\nContact us at guy@ideaguybrands.com to learn more.'
    },
    'trades': {
      keywords: ['accurate', 'accuracy', 'reliable', 'precision', 'quality', 'trade', 'trades', 'service', 'services'],
      response: 'HomeFixer360 supports 8+ home service trades out of the box:\n\n• Kitchens & Baths\n• Windows & Doors\n• Decks & Fencing\n• Driveway Repaving\n• Electrical\n• HVAC\n• Hardscaping\n• Home Renovations\n\nYou can also add your own service categories in settings.'
    },
    'users': {
      keywords: ['team', 'user', 'account', 'multi-user', 'users', 'seats'],
      response: 'Team accounts available:\n\n**Basic**: Up to 3 users\n**Professional**: Up to 7 users\n**Elite**: Unlimited users\n\nAll team members get their own login, and you can manage permissions from your dashboard!'
    },
    'support': {
      keywords: ['support', 'help', 'assistance', 'contact', 'phone', 'email'],
      response: 'Support varies by plan:\n\n**Basic**: AI Chatbot (me!) 24/7\n**Professional**: Email support + chatbot\n**Elite**: Priority phone support + email + chatbot\n\nYou can also email guy@ideaguybrands.com or call (833) 962-2742 for sales questions!'
    },
    'branding': {
      keywords: ['branding', 'white-label', 'logo', 'custom', 'brand'],
      response: 'Branding options:\n\n**Basic**: Includes "Created by HomeFixer360" on estimates\n**Professional**: Remove our branding, add your logo\n**Elite**: Full white-label, completely branded as your company\n\nUpload your logo in Profile Settings once subscribed!'
    },
    'features': {
      keywords: ['feature', 'what can', 'capabilities', 'functions', 'do'],
      response: 'Key features:\n\n✅ Line-item estimator for any trade\n✅ Built-in CRM & pipeline\n✅ Branded PDF proposals\n✅ Customer e-sign + online payments\n✅ Team collaboration\n✅ Custom branding (Pro+)\n✅ API access (Elite)\n\nAll designed for multi-trade home service businesses.'
    },
    'partner': {
      keywords: ['partner', 'partnership', 'reseller', 'referral', 'commission'],
      response: 'We have a Partner Program with great commissions:\n\n**Referral Partners**: 30% recurring commission\n**Reseller Partners**: 60% recurring commission\n\nVisit /partner-program to learn more and apply!'
    },
    'contact': {
      keywords: ['contact', 'reach', 'call', 'email', 'talk'],
      response: 'You can reach us:\n\n📧 Email: guy@ideaguybrands.com\n📞 Phone: (833) 962-2742\n🌐 Contact Form: /contact\n\nWe typically respond within 24 hours!'
    }
  };

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    // Check each knowledge base entry
    for (const [key, data] of Object.entries(knowledgeBase)) {
      if (data.keywords.some(keyword => lowerMessage.includes(keyword))) {
        return data.response;
      }
    }

    // Default response
    return "I'm not sure about that. Here are some things I can help with:\n\n• Pricing and plans\n• How HomeFixer360 works\n• Supported trades (kitchens, baths, HVAC, decks, etc.)\n• Team accounts\n• Support options\n• Branding and white-label\n\nOr contact us directly at guy@ideaguybrands.com for detailed questions!";
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      text: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    // Simulate typing delay
    setTimeout(() => {
      const botResponse = getBotResponse(inputText);
      const botMessage = {
        type: 'bot',
        text: botResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  const handleQuickQuestion = (question) => {
    setInputText(question);
    handleSendMessage();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-blue-600 to-green-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center z-50 animate-pulse"
        >
          <MessageCircle className="w-8 h-8" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-500 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">HomeFixer360 Assistant</h3>
                <p className="text-xs text-blue-100">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-2 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' ? 'bg-blue-600' : 'bg-green-500'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div className={`rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {message.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 1 && (
            <div className="p-3 border-t border-gray-200 bg-white">
              <p className="text-xs text-gray-600 mb-2 font-medium">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded-full transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
